import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { Button } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { seatingChartsKey } from '../../../lib/groupQueryKeys';
import {
  buildDinnerChangeOffer,
  conflictNote,
  dinnerEditZone,
  hasGuestMeaningfulChange,
  unlinkedSeatsLine,
  type DinnerChangeOffer,
} from '../../../lib/dinnerEdit';
import type { ClaimEditNote } from '../../../lib/dinnerClaimEdit';
import ChangeNoticeDialog from '../changes/ChangeNoticeDialog';
import EditDinnerSheet from './EditDinnerSheet';
import type { GroupDetail, GroupDinnerHold, GroupInvitee, UpdateDinnerHoldResponse } from '../../../types/groups';

/**
 * The host's way to correct a dinner: an Edit button, the sheet it opens, and —
 * after a save that guests would notice — the offer to tell them.
 *
 * Three things happen after a successful save, in this order, and none of them
 * is a send:
 *  1. The holds (which carry the claims) and the seating charts (which read the
 *     dinner's venue and time) are re-read.
 *  2. Same-evening conflicts are WARNED about, never blocked on — the edit is
 *     already done and no claim was touched.
 *  3. When a guest-visible field changed and somebody holds a seat, the change
 *     notice dialog opens, addressed to the claimants only (`inviteeIds` from
 *     the server's `claimantInviteeIds`). The dialog's default is "Don't send".
 */
export default function EditDinner({
  group,
  hold,
  roster,
  tripZone,
  tripZoneIsFallback,
  onNote,
}: {
  group: GroupDetail;
  /** The live hold — its claimed count feeds the pre-check. */
  hold: GroupDinnerHold;
  roster: GroupInvitee[];
  tripZone: string;
  tripZoneIsFallback: boolean;
  /** A note that outlives the sheet, shown on the dinner's card. */
  onNote: (note: ClaimEditNote | null) => void;
}) {
  const queryClient = useQueryClient();
  /*
    A snapshot taken when the sheet opens, not the live hold. The diff and
    `expectedSourceVersion` have to be against what the host was LOOKING AT: if
    a background refetch swapped in someone else's edit under an open sheet, the
    host would be saving over a change they never saw, with a version that made
    it look deliberate.
  */
  const [editing, setEditing] = useState<GroupDinnerHold | null>(null);
  const [offer, setOffer] = useState<DinnerChangeOffer | null>(null);

  const refresh = () => {
    queryClient.invalidateQueries(['group-dinner-holds', group.id]);
    // Charts read the dinner's venue and time at read time, so a chart on screen is now behind.
    queryClient.invalidateQueries(seatingChartsKey(group.id));
    queryClient.invalidateQueries(['seating-chart', group.id]);
  };

  const handleSaved = (before: GroupDinnerHold, result: UpdateDinnerHoldResponse) => {
    setEditing(null);
    refresh();

    const changedFields = result.changedFields ?? [];
    if (changedFields.length === 0) {
      onNote({ severity: 'info', message: 'Nothing changed — the dinner already read that way.', status: '' });
      return;
    }

    const conflicts = conflictNote(result.sameEveningConflicts ?? [], result.hold, roster);
    const made = buildDinnerChangeOffer({
      before,
      after: result.hold,
      tripZone,
      changedFields,
      claimantInviteeIds: result.claimantInviteeIds,
      unlinkedClaimSeats: result.unlinkedClaimSeats,
    });
    // Guests would notice, but nobody holds a seat that can be messaged: say so
    // rather than let the host assume a notice went somewhere.
    const nobodyToTell = !made && hasGuestMeaningfulChange(changedFields)
      ? unlinkedSeatsLine(result.unlinkedClaimSeats ?? 0)
      : null;

    onNote(conflicts
      ? { severity: 'warning', message: `Saved. ${conflicts}`, status: '' }
      : {
        severity: 'success',
        message: `Saved. Every claim and the seating stay as they were.${nobodyToTell ? ` ${nobodyToTell}` : ''}`,
        status: '',
      });
    if (made) setOffer(made);
  };

  return (
    <>
      <Button
        size="small"
        startIcon={<EditOutlinedIcon fontSize="small" />}
        aria-label={`Edit ${hold.venueName}`}
        onClick={() => { onNote(null); setEditing(hold); }}
      >
        Edit
      </Button>
      {editing ? (
        <EditDinnerSheet
          key={`${editing.id}:${editing.sourceVersion ?? ''}`}
          groupId={group.id}
          hold={editing}
          claimedSeats={hold.claimedSpots}
          zone={dinnerEditZone(editing, tripZone)}
          zoneIsFallback={tripZoneIsFallback && !editing.timeZone}
          onClose={() => setEditing(null)}
          onSaved={(result) => handleSaved(editing, result)}
          onReload={() => {
            setEditing(null);
            refresh();
            onNote({ severity: 'info', message: 'Reloaded the latest — open Edit again to change it.', status: '' });
          }}
        />
      ) : null}
      <ChangeNoticeDialog
        open={Boolean(offer)}
        onClose={() => setOffer(null)}
        group={group}
        notice={offer?.notice ?? null}
        audience={offer
          ? {
            inviteeIds: offer.audience.inviteeIds,
            label: offer.audience.label,
            note: offer.audience.unlinkedNote,
          }
          : undefined}
        undoHint="edit the dinner back"
      />
    </>
  );
}
